import {OutsourcedSymbol, SymbolArgs} from "./symbols/OutsourcedSymbol";
import {gsap, Power2} from "gsap"
import {Outsourced} from "../Outsourced";
import {OutsourcedXWaysSymbol} from "./symbols/XWaysSymbol";

export class BasedMiniReel extends PIXI.Container {

    public reelID:number;

    public symbols:OutsourcedSymbol[];
    public symbolsContainer:PIXI.Container;
    public readonly stepSize = 148;
    public readonly stepDuration = 0.028;
    public shouldRespin:boolean = false;

    public reelSet:string[];

    public visibleSymbols:number;

    public isSpinning:boolean = false;
    public spinTimeline:gsap.core.Timeline;

    public bounceFactor:number = 0.1;
    public stopEase:string = "none";
    public stopSymbol:OutsourcedSymbol;

    public stepData = {
        y: 0
    };

    constructor(reelID:number, visibleSymbols:number) {
        super();

        this.name = "SkateOrDieReel";
        this.visibleSymbols = visibleSymbols;
        this.reelID = reelID;

        this.symbolsContainer = new PIXI.Container();
        this.symbolsContainer.name = "Symbols";
        this.addChild(this.symbolsContainer);

        const mask = new PIXI.Graphics;
        mask.beginFill();
        mask.drawRect(-72, -72, 148, 148);
        this.mask = mask;
        this.addChild(this.mask);
    }

    public insertSymbols(symbols:OutsourcedSymbol[]):void {
        this.symbolsContainer.removeChildren(0, this.symbolsContainer.children.length);
        this.symbols = symbols;
        this.symbolsContainer.addChild(...this.symbols);
        this.updateSymbolsPosition();
    }

    public extractSymbols():OutsourcedSymbol[] {
        this.symbolsContainer.removeChildren(0, this.symbolsContainer.children.length);
        const oldSymbols = this.symbols;
        this.symbols = [];
        return oldSymbols;
    }

    public start():gsap.core.Timeline {
        const tl = gsap.timeline();

        const tween = this.step("back.in(3)");
        tween.totalDuration(this.stepDuration * 8);
        tl.add(tween);
        tl.add(() => {
            this.isSpinning = true;
            this.symbols.forEach((sym) => sym.setSpinning());
            this.playSpin();
        });
        return tl;
    }

    public stop(stopSymbol:string, ease:string="none", bounceFactor:number = 0.2): gsap.core.Timeline {
        this.stopSymbol = Outsourced.symbolFactory.createSymbol({symbolName: stopSymbol,
                                                                     reelID: this.reelID,
                                                                     reelPosition: -1});


        const hackjob = gsap.timeline({paused:true}).add(this.playStop());
        return gsap.timeline()
            .add(() => {
                // Stop spin timeline
                this.bounceFactor = bounceFactor;
                this.stopEase = ease;
                this.spinTimeline.repeat(0);
                this.spinTimeline.eventCallback("onComplete", () => {
                    hackjob.play(0);
                })
            })
            .add(() => {
                this.spinTimeline.kill();
                hackjob.progress(1);
            }, '>0.1')
    }

    private playSpin() {
        this.spinTimeline = gsap.timeline();
        this.spinTimeline.add(this.step());
        this.spinTimeline.repeat(-1);
    }

    private playStop() {
        const stopTL = gsap.timeline();

        const totalSteps = this.visibleSymbols;
        const stopDuration = (totalSteps + 1) * this.stepDuration;

        // Add stop symbols to reel
        stopTL.add(() => {
            this.symbols.unshift(this.stopSymbol);
            this.symbolsContainer.addChild(this.stopSymbol);
            this.updateSymbolsPosition();
            this.symbols.forEach((sym) => sym.setStill());
        });
        // Spin in stop symbols
        stopTL.add(gsap.to(this.stepData, {
            y: this.stepSize * totalSteps,
            duration: stopDuration,
            ease: this.stopEase,
            onUpdate: () => {
                this.updateSymbolsPosition();
            },
        }))
        stopTL.add(() => {
            this.symbols.forEach((sym) => {
                sym.reelPosition += 1;
            });
            this.updateSymbolsPosition();
            this.stepData.y = 0;
        })

        const bounceDuration = 0.1;

        // Add bounce
        stopTL.add(gsap.to(this.stepData, {
            y: this.stepSize * this.bounceFactor,
            duration: bounceDuration,
            ease: "power2.out",
            onUpdate: () => {
                this.updateSymbolsPosition();
            }
        }))
        stopTL.add(gsap.to(this.stepData, {
            y: 0,
            duration: bounceDuration,
            ease: "power2.out",
            onUpdate: () => {
                this.updateSymbolsPosition();
            }
        }))

        stopTL.add(() => {
            this.isSpinning = false;
        })

        return stopTL;
    }

    // Moves all symbols one step
    private step(ease:string = "none"):gsap.core.Tween {
        return gsap.to(this.stepData, {
            y: this.stepSize,
            ease,
            duration: this.stepDuration,
            onStart: () => {
                this.addRandomSymbol(-1);
                this.symbols[0].setSpinning(true);
            },
            onUpdate: () => {
                this.updateSymbolsPosition();
            },
            onComplete: () => {
                this.stepData.y = 0;
                this.symbols.forEach(sym => sym.reelPosition++);

                while (this.symbols[this.symbols.length - 1].reelPosition > 0) {
                    this.symbolsContainer.removeChild(this.symbols[this.symbols.length - 1]);
                    this.symbols.pop();
                }
            }
        });
    }

    public getSymbols():OutsourcedSymbol[] {
        return this.isSpinning ? [this.stopSymbol] : this.symbols;
    }

    private updateSymbolsPosition() {
        this.symbols.forEach(sym => {
            const basePos = this.stepSize * sym.reelPosition;
            sym.position.y = basePos + this.stepData.y;
            sym.position.x = 0;
        })
    }

    private getRandomSymbolInReelSet():string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        return this.reelSet[randomIndex];
    }

    private addRandomSymbol(reelPos:number) {
        const name = this.getRandomSymbolInReelSet()
        const symbol = Outsourced.symbolFactory.createSymbol({symbolName: name,
                                                                  reelID: this.reelID,
                                                                  reelPosition: reelPos});
        this.symbols.unshift(symbol);
        this.symbolsContainer.addChild(symbol);
    }
}